<template>
  <div
    v-if="marketingCookie"
    class="sponsored-products"
  >
    <BasicSkeleton :show-placeholder="showPlaceholder">
      <ais-instant-search
        v-if="requestStatus === 'success'"
        :search-client="searchClient"
        :index-name="indexName"
      >
        <ais-configure
          :hits-per-page.camel="limit"
          :filters="searchFilter"
        >
        </ais-configure>
        <ais-hits :transform-items="transformItems">
          <template #default="{ items }">
            <slot
              :sponsored-items="items"
              :on-load-beacon-link="sponsoredArticles?.onLoadBeacon"
              :on-view-beacon-link="sponsoredArticles?.onViewBeacon"
            ></slot>
          </template>
        </ais-hits>
      </ais-instant-search>
      <template #placeholder>
        <slot name="placholder"></slot>
      </template>
    </BasicSkeleton>
  </div>
</template>

<script setup lang="ts">
import { AisInstantSearch, AisConfigure, AisHits } from 'vue-instantsearch/vue3/es';
import type { AisArticle, SponsoredArticle, SponsoredSearchResult } from '@expert/types';
import type { SponsoredArticleHit } from '~~/models/AisSearch/Result';
import { usePlacementBeacon } from '~/composables/sponsoring';

interface Props {
  pageType: PageType;
  articleSkuList?: Array<string>;
  categoryIds?: Array<string>;
  searchWord?: string;
  brands?: Array<string>;
  limit?: number;
}

const props = withDefaults(defineProps<Props>(), {
  articleSkuList: () => [],
  categoryIds: () => [],
  searchWord: undefined,
  brands: () => [],
  limit: 12,
});

const searchClient = useNuxtApp().$searchClient('/api/search/ppm-category');
const indexName = useReactiveSearchCreds().reactiveBaseAppName;
const marketingCookie = getCookiePermissions(PermissionMapping.MARKETING);
const showPlaceholder: Ref<boolean> = ref(true);

const articleSKUs: ComputedRef<Array<string> | null> = computed(() => {
  let tmpSkuList: Array<string>;
  if (props.articleSkuList && props.articleSkuList.length > 0) {
    tmpSkuList = props.articleSkuList;
  } else {
    tmpSkuList = gtmDataContainer().value?.ecommerce?.impressions?.map((impression) => impression?.id) ?? [];
  }

  return tmpSkuList.filter(Boolean);
});

const { data: sponsoredArticles, status: requestStatus } = await useFetch<SponsoredSearchResult>(
  `${useRuntimeConfig().public.apiUrl}/api/neo/article/getSponsoredArticles`,
  {
    key: `sponsored-articles/${props.pageType.toLowerCase()}`,
    method: 'POST',
    body: {
      user: useCurrentUserState().value,
      institutionId: useCurrentStoreId(),
      pageType: props.pageType,
      device: {
        userAgent: useDevice().userAgent,
        isMobile: useDevice().isMobile,
      },
      categoryIds: props.categoryIds,
      searchWord: props.searchWord,
      articleSkuList: articleSKUs.value,
      filteredManufacturers: props.brands,
    },
  },
);

const searchFilter: Ref<string> = ref('articleNumber:1');
const beaconMap: Ref<Map<string, SponsoredArticle>> = ref(new Map());
let placementBeaconStringSlot: string = '&slot=';
let placementBeaconStringSku: string = '&sku=';
const { placementBeaconString } = usePlacementBeacon();


watch(
  sponsoredArticles,
  () => {

    const webcodes: Array<string> = [];
    let index = 0;
    for (const sponsoredArticle of sponsoredArticles.value?.articles ?? []) {
      placementBeaconStringSlot += `${index}|`;
      placementBeaconStringSku += `${sponsoredArticle.articleReference}|`;
      index++;
      webcodes.push(sponsoredArticle.articleReference);
      beaconMap.value.set(sponsoredArticle.articleReference, sponsoredArticle);
    }
    placementBeaconStringSku = placementBeaconStringSku.slice(0, -1); // Remove the trailing '|'
    placementBeaconStringSlot = placementBeaconStringSlot.slice(0, -1); // Remove the trailing '|'
    placementBeaconString.value = placementBeaconStringSku + placementBeaconStringSlot;
    switch (webcodes.length) {
      case 0:
        searchFilter.value = 'articleNumber:1';
        break;
      case 1:
        searchFilter.value = 'articleNumber:' + webcodes.at(0);
        break;
      default:
        searchFilter.value = 'articleNumber:(' + webcodes.join(' OR ') + ')';
        break;
    }
  },
  { immediate: true },
);

const transformItems = (items: Array<AisArticle>): Array<SponsoredArticleHit> => {
  showPlaceholder.value = false;
  return items.map((articleHit: AisArticle) => {
    return {
      ...articleHit,
      beacons: beaconMap.value.get(articleHit.articleView.articleNumber),
    } as SponsoredArticleHit;
  });
};
</script>

<style scoped></style>
